import {
  Alert,
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel
} from "@mui/material";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import emailjs from 'emailjs-com';
import SectionHeader from "../components/SectionHeader";

const bedAmounts = [1, 2, 3, 4, 5];
const bathAmounts = [1, 1.5, 2, 2.5, 3, 3.5, 4];
const packages = [
  { value: "standard", label: "Standard" },
  { value: "professional", label: "Professional" },
  { value: "whiteglove", label: "White-Glove" },
];

interface ContactProps {
  bgcolor?: string;
  preselectedPackage?: string;
}

function Contact({ bgcolor, preselectedPackage }: ContactProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    postalCode: "",
    beds: "",
    baths: "",
    package: preselectedPackage || "",
    details: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<"error" | null>(null);

  useEffect(() => {
    if (preselectedPackage) {
      setFormData(prev => ({ ...prev, package: preselectedPackage }));
    }
  }, [preselectedPackage]);

  useEffect(() => {
    const packageParam = searchParams.get("package");
    if (packageParam && packages.some(p => p.value === packageParam)) {
      setFormData(prev => ({ ...prev, package: packageParam }));
    }
  }, [searchParams]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const fullMessage = `
          Email: ${formData.email}
          Phone: ${formData.phone}
          PostalCode: ${formData.postalCode}
          Beds: ${formData.beds}
          Baths: ${formData.baths}
          Package: ${formData.package ? packages.find(p => p.value === formData.package)?.label : 'Not selected'}
          Details: ${formData.details}
          `.trim();

    const templateParams = {
      name: formData.name,
      email: formData.email,
      message: fullMessage,
    };



    emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID!,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
      templateParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
    )
      .then(() => {
        setSubmitted(true);
        navigate("/thank-you");
      })
      .catch(() => {
        setStatus("error");
        setSubmitted(false);
        setTimeout(() => {
          setStatus(null);
        }, 5000);
      });
  };

  return (
    <>
      <Box sx={{ width: "100%", bgcolor }}>
        <Container id="quote" component="section" maxWidth="md" sx={{ py: 4 }}>
          <Box sx={{ textAlign: "center" }}>
            <SectionHeader
              title="Get a Quote"
              subtitle="Provide a few quick details and we'll send you a personalized quote for AirBnB cleaning services in Toronto."
            />
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Left Column */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Name"
                  name="name"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Email"
                  name="email"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Phone"
                  name="phone"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>

              {/* Postal Code */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Postal Code"
                  name="postalCode"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formData.postalCode}
                  onChange={handleChange}
                />
              </Grid>

              {/* Bed & Bath Dropdowns */}
              <Grid item xs={6} sm={4}>
                <TextField
                  select
                  label="Beds"
                  name="beds"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formData.beds}
                  onChange={handleChange}
                >
                  {bedAmounts.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  select
                  label="Baths"
                  name="baths"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={formData.baths}
                  onChange={handleChange}
                >
                  {bathAmounts.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Message Field (Full Width) */}
              <Grid item xs={12}>
                <TextField
                  label="Additional details"
                  name="details"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}  // Reduced height by 50%
                  size="small"
                  value={formData.details}
                  onChange={handleChange}
                />
              </Grid>

            </Grid>

            {/* Package Selection */}
            <Box sx={{ mt: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: 2 }}>
              <Typography component="label" sx={{ fontWeight: 500 }}>
                Package:
              </Typography>
              <RadioGroup
                row
                name="package"
                value={formData.package}
                onChange={handleChange}
              >
                {packages.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={option.label}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: { xs: "0.85rem", sm: "1rem" }
                      }
                    }}
                  />
                ))}
              </RadioGroup>
            </Box>

            {status && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
              >
                Failed to send message. Please try again or call us.
              </Alert>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitted}
              sx={{ mt: 2 }}
            >
              Submit
            </Button>
          </form>
        </Container>

      </Box>

      {/* <Snackbar
        open={snackbarOpen}
        autoHideDuration={15000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={(props) => <Slide {...props} direction="left" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarStatus}
          sx={{
            width: '500px',
            whiteSpace: 'pre-line',
            textAlign: 'left',
            '& .MuiAlertTitle-root': {
              fontWeight: 'bold',
              color: 'inherit',
            },
          }}
        >
          <AlertTitle>{snackbarStatus === 'success' ? 'Success' : 'Error'}</AlertTitle>
          {snackbarStatus === 'success'
            ? 'Message sent successfully! \nOur team will contact you shortly.'
            : 'Failed to send message. \nPlease try again or call us.'}
        </Alert>
      </Snackbar> */}
    </>
  );
}

export default Contact;
